import { FlatStringStorage } from "../../../wgs/scene/InstanceTreeStorage";
import { AttributeType } from "../../lmvtk/common/PropdbEnums";
import { isStringType, fail } from "./common";
export const Column = {
    fromWireType(idCount, attr, descriptor, storage) {
        const metadata = descriptor[2];
        const isSparse = metadata.isSparse;
        if (isSparse) {
            return new SparseColumn(attr, descriptor[0], storage, descriptor[1]);
        }
        const nullMarkerCol = descriptor ? new NullableColumnMarker(0, descriptor[1]) : undefined;
        const colData = descriptor[0];
        const size = idCount;
        const type = attr.dataType;
        const isStringType = type >= 20;
        if (isStringType) {
            return StringColumn.fromWireType(attr, colData, storage);
        }
        switch (type) {
            case AttributeType.Boolean: return new BoolColumn(attr, size, colData, nullMarkerCol);
            case AttributeType.Integer: return new IntCol(attr, size, colData, nullMarkerCol);
            case AttributeType.Float: return new Float32Col(attr, size, colData, nullMarkerCol);
            case AttributeType.DbKey: return new Uint32Col(attr, size, colData, nullMarkerCol);
            case AttributeType.Double: return new DoubleCol(attr, size, colData, nullMarkerCol);
            default: throw new Error(`Attribute not supported: [ ${attr.category}/${attr.name} ]`);
        }
    },
    fromAttribute(attr, size, isSparse) {
        if (isSparse) {
            return new SparseColumn(attr);
        }
        const type = attr.dataType;
        if (isStringType(attr)) {
            return new StringColumn(size, attr);
        }
        switch (type) {
            case AttributeType.Boolean: return new BoolColumn(attr, size);
            case AttributeType.Integer: return new IntCol(attr, size);
            case AttributeType.Float: return new Float32Col(attr, size);
            case AttributeType.DbKey: return new Uint32Col(attr, size);
            case AttributeType.Double: return new DoubleCol(attr, size);
            default: throw new Error(`Attribute not supported: [ ${attr.category}/${attr.name} ]`);
        }
    }
};
export class BoolColumn {
    idCount;
    #col;
    #view;
    #attr;
    nullTable;
    constructor(attr, idCount, storage, flags) {
        this.idCount = idCount;
        this.#attr = attr;
        this.#col = storage ?? new Uint8Array(Math.ceil(idCount / 8));
        this.#view = new DataView(this.#col.buffer);
        this.nullTable = flags ?? new NullableColumnMarker(idCount);
    }
    getValueAt(index) {
        if (this.nullTable.isNull(index))
            return undefined;
        return getMask(index, this.#view);
    }
    setValueAt(index, valueId, pdb) {
        const value = pdb.getAttrValue(this.#attr.attrId, valueId);
        if (valueId === undefined || value === undefined) {
            return;
        }
        this.nullTable.setNotNull(index);
        if (index >= this.idCount)
            return;
        setMask(index, value, this.#view);
    }
    toWireType() {
        return [this.#col, this.nullTable.flags, { isSparse: false }];
    }
    byteCount() {
        return this.#col.byteLength + this.nullTable.flags.byteLength;
    }
    index(pdb, storage, valIdToStorage) {
        return [storage, valIdToStorage];
    }
    isNull(index) {
        return this.nullTable.isNull(index);
    }
    asDictionaryType() {
        throw new Error(`Not a dictionary type`);
    }
}
// Only supports flagging indices as not null
class NullableColumnMarker {
    flags;
    #view;
    constructor(idCount, storage) {
        this.flags = storage ?? new Uint8Array(Math.ceil(idCount / 8));
        this.#view = new DataView(this.flags.buffer);
    }
    setNotNull(index) {
        setMask(index, true, this.#view);
    }
    isNull(index) {
        return !getMask(index, this.#view);
    }
}
// Int32 ranges
const MAX_INT32 = 2147483647;
const MIN_INT32 = -2147483648;
export class BaseCol {
    idCount;
    #col;
    #view;
    #getter;
    #setter;
    #attr;
    #nullTable;
    constructor(attr, buf, getter, setter, flags) {
        this.#attr = attr;
        this.#nullTable = flags ?? new NullableColumnMarker(buf.length);
        this.#getter = getter;
        this.#setter = setter;
        this.#col = buf;
        this.#view = new DataView(buf.buffer);
        this.idCount = buf.length;
    }
    get attr() {
        return this.#attr;
    }
    getValueAt(index) {
        if (this.#nullTable.isNull(index))
            return undefined;
        return this.#getter(this.#view, index);
    }
    setValueAt(index, valueId, pdb) {
        const value = pdb.getAttrValue(this.#attr.attrId, valueId);
        if (valueId === undefined || value === undefined) {
            return;
        }
        this.#nullTable.setNotNull(index);
        this.#setter(this.#view, index, value);
    }
    setValue(index, value) {
        if (value === undefined) {
            return;
        }
        this.#nullTable.setNotNull(index);
        this.#setter(this.#view, index, value);
    }
    toWireType() {
        return [this.#col, this.#nullTable.flags, { isSparse: false }];
    }
    byteCount() {
        return this.#col.byteLength + this.#nullTable.flags.byteLength;
    }
    index(pdb, storage, valIdToStorage) {
        return [storage, valIdToStorage];
    }
    isNull(index) {
        return this.#nullTable.isNull(index);
    }
    asDictionaryType() {
        throw new Error(`Not a dictionary type`);
    }
}
export class Int32Col extends BaseCol {
    constructor(attr, idCount, storage, flags) {
        super(attr, storage ?? new Int32Array(idCount), (v, i) => v.getInt32(i * 4, true), (view, i, v) => view.setInt32(i * 4, Number(v), true), flags);
    }
}
export class Int64Col extends BaseCol {
    constructor(attr, idCount, storage, flags) {
        super(attr, storage ?? new BigInt64Array(idCount), (v, i) => v.getBigInt64(i * 8, true), (view, i, v) => view.setBigInt64(i * 8, BigInt(v), true), flags);
    }
}
// Strategy interface to upgrade to BigInt storage as needed at runtime.
export class IntCol {
    #intStrategy;
    idCount;
    constructor(attr, idCount, storage, flags) {
        // Initialize 32 bit strategy by default
        if (storage instanceof BigInt64Array) {
            this.#intStrategy = new Int64Col(attr, idCount, storage, flags);
        }
        else {
            this.#intStrategy = new Int32Col(attr, idCount, storage, flags);
        }
    }
    #upgradeToBigInt() {
        const oldStrategy = this.#intStrategy;
        const newStrategy = new Int64Col(oldStrategy.attr, oldStrategy.idCount, undefined, undefined);
        // Copy all values from Int32 to Int64
        for (let i = 0; i < oldStrategy.idCount; i++) {
            const val = oldStrategy.getValueAt(i);
            if (val !== undefined) {
                newStrategy.setValue(i, val);
            }
        }
        this.#intStrategy = newStrategy;
    }
    getValueAt(index) {
        // BigInt support is limited in LMV that uses these values. Cast value @ index back to the number in case it is BigInt.
        // This looses accuracy for values > 2**53-1 or < -2**53 but is necessary for compatibility.
        const value = this.#intStrategy.getValueAt(index);
        return Number(value);
    }
    setValueAt(index, valueId, pdb) {
        const value = pdb.getAttrValue(this.#intStrategy.attr?.attrId, valueId);
        if (valueId === undefined || value === undefined) {
            return;
        }
        // Check if we need to upgrade to BigInt
        const isInt32Safe = value >= MIN_INT32 && value <= MAX_INT32;
        if (!isInt32Safe && this.#intStrategy instanceof Int32Col) {
            this.#upgradeToBigInt();
        }
        this.#intStrategy.setValue(index, value);
    }
    toWireType() {
        return this.#intStrategy.toWireType();
    }
    byteCount() {
        return this.#intStrategy.byteCount();
    }
    index(pdb, storage, valIdToStorage) {
        return this.#intStrategy.index(pdb, storage, valIdToStorage);
    }
    isNull(index) {
        return this.#intStrategy.isNull(index);
    }
    asDictionaryType() {
        return this.#intStrategy.asDictionaryType();
    }
}
export class Uint32Col extends BaseCol {
    constructor(attr, idCount, storage, flags) {
        super(attr, storage ?? new Uint32Array(idCount), (v, i) => v.getUint32(i * 4, true), (view, i, v) => view.setUint32(i * 4, Number(v), true), flags);
    }
}
// Float type exists in LMVTK PDB but not in the C++ one... I've never seen this in the wild
export class Float32Col extends BaseCol {
    constructor(attr, idCount, storage, flags) {
        super(attr, storage ?? new Float32Array(idCount), (v, i) => v.getFloat32(i * 4, true), (view, i, v) => view.setFloat32(i * 4, Number(v), true), flags);
    }
}
export class DoubleCol extends BaseCol {
    constructor(attr, idCount, storage, flags) {
        // somewhat a hack casting here, either that or coming up with a name that spans these number types
        super(attr, (storage ?? new Float64Array(idCount)), (v, i) => v.getFloat64(i * 8, true), (view, i, v) => view.setFloat64(i * 8, Number(v), true), flags);
    }
}
// String column works the same as the Uint column, except it manages a FlatStringStorage instance for de-duplication
export class StringColumn {
    idCount;
    attrDef;
    isStringType = true;
    #col;
    #view;
    #stringStorage;
    get isIndexed() { return Boolean(this.#stringStorage); }
    constructor(idCount, attrDef, data) {
        this.idCount = idCount;
        this.attrDef = attrDef;
        this.#col = data ?? new Uint32Array(idCount);
        this.#view = new DataView(this.#col.buffer);
    }
    /**
     * Useful for remapping value IDs from the PDB into a FlatStringStorage index
     * @param pdb
     * @param storage
     * @param valIdToStorage
     */
    index(pdb, storage, valIdToStorage) {
        if (this.isIndexed)
            throw new Error(`Cannot index twice`);
        this.#stringStorage = storage ?? new FlatStringStorage();
        const valIdToSs = valIdToStorage ?? new Map();
        for (let i = 0; i < this.idCount; i++) {
            const valId = this.#view.getUint32(i * 4, true);
            if (valId === 0)
                continue;
            const existing = valIdToSs.get(valId);
            if (existing) {
                this.#view.setUint32(i * 4, existing, true);
                continue;
            }
            const str = pdb.getAttrValue(this.attrDef.attrId, valId);
            const ssIdx = this.#stringStorage.add(str);
            valIdToSs.set(valId, ssIdx);
            this.#view.setUint32(i * 4, ssIdx, true);
        }
        return [this.#stringStorage, valIdToSs];
    }
    /**
     * This should be used if external indexing is provided
     * @param storage
     */
    setDictionary(storage) {
        this.#stringStorage = storage;
    }
    getValueAt(index) {
        if (!this.isIndexed)
            return '';
        return this.#stringStorage.get(this.#view.getUint32(index * 4, true));
    }
    setValueAt(index, valueId) {
        if (!valueId)
            return; // 0 will just be the null string
        // should this class be a uint32 return type with an additional function specifically for getting the string or?
        if (this.isIndexed)
            throw new Error(`Write after indexing`);
        this.#view.setUint32(index * 4, valueId, true);
    }
    isNull(index) {
        return this.#view.getUint32(index * 4, true) === 0;
    }
    toWireType() {
        return [this.#col, new Uint8Array(0), { isSparse: false }]; // Strings wired separately
    }
    getDictionary() {
        return this.#stringStorage;
    }
    byteCount() {
        return this.#col.byteLength;
    }
    valueIdAtIndex(index) {
        if (index < 0 || index >= this.idCount)
            return 0;
        return this.#view.getUint32(index * 4, true);
    }
    asDictionaryType() {
        return this;
    }
    static fromWireType(attrDef, buf, storage) {
        const col = new StringColumn(buf.length, attrDef, buf);
        col.#stringStorage = storage;
        return col;
    }
}
/**
 * Sparse column should be used when the cost of contiguous arraybuffers
 * is higher than using a simple number->number map. This is not determined
 * here, but is done in a query indexing step, or dynamically predicted during the filter query
 */
class SparseColumn {
    isSparse = true;
    #values = new Map();
    #stringStorage;
    #descriptor;
    get idCount() {
        return this.#values.size;
    }
    #byteCount;
    #attr;
    isStringType;
    constructor(attr, data, storage, indexTable) {
        if (attr.dataType === AttributeType.Boolean)
            throw new Error("Not supported: Boolean");
        if (data && indexTable) {
            let buf = new Uint32Array(data.buffer);
            if (attr.dataType === AttributeType.Integer) {
                buf = new Int32Array(data.buffer);
            }
            else if (attr.dataType === AttributeType.Float) {
                buf = new Float32Array(data.buffer);
            }
            else if (attr.dataType === AttributeType.Double) {
                buf = new Float64Array(data.buffer);
            }
            const indexBuf = new Uint32Array(indexTable.buffer);
            this.#values = new Map();
            for (let i = 0; i < buf.length; i++) {
                this.#values.set(indexBuf[i], buf[i]);
            }
        }
        this.#byteCount = data?.byteLength ?? 0;
        this.#stringStorage = storage;
        this.#attr = attr;
        this.isStringType = attr.dataType >= 20;
    }
    isNull(index) {
        if (this.isStringType) {
            return !this.#values.has(index) || this.#values.get(index) === 0;
        }
        return this.#values.has(index);
    }
    index(pdb, storage, valIdToStorage) {
        if (isStringType(this.#attr)) {
            this.#stringStorage = storage;
        }
        const attr = this.#attr;
        const dummyPdb = {
            getAttrValue(aid, vid) {
                // For strings, it's just a dummy PDB that just passes back the vid just like direct storage does
                return isStringType(attr) ? vid : pdb.getAttrValue(attr.attrId, vid);
            }
        };
        const dummyIndexPdb = {
            getAttrValue: (aid, vid) => vid
        };
        // Convert to optimized buffer form by using existing columns:
        //    [ Specialized_DataType, Index ]
        const specializedColumn = Column.fromAttribute(this.#attr, this.#values.size);
        const indexColumn = Column.fromAttribute({ dataType: AttributeType.DbKey }, this.#values.size);
        let i = 0;
        for (const [index, value] of this.#values) {
            specializedColumn.setValueAt(i, value, dummyPdb);
            indexColumn.setValueAt(i, index, dummyIndexPdb);
            i++;
        }
        // Has provided storage
        if (this.isStringType && this.#stringStorage) {
            specializedColumn.asDictionaryType().setDictionary(this.#stringStorage);
        }
        else {
            specializedColumn.index(pdb, storage, valIdToStorage);
        }
        const specializedDescriptor = specializedColumn.toWireType();
        const indexDescriptor = indexColumn.toWireType();
        this.#descriptor = [
            new Uint8Array(specializedDescriptor[0].buffer),
            new Uint8Array(indexDescriptor[0].buffer),
            { isSparse: true }
        ];
        return [storage, valIdToStorage];
    }
    getValueAt(index) {
        if (isStringType(this.#attr)) {
            return this.#stringStorage?.get(this.#values.get(index));
        }
        else {
            return this.#values.get(index);
        }
    }
    setValueAt(index, valueId) {
        if (typeof valueId === 'undefined' || valueId === null)
            return;
        this.#values.set(index, valueId);
    }
    valueIdAtIndex(index) {
        return this.#values.get(index) ?? 0;
    }
    toWireType() {
        if (!this.#descriptor && this.isStringType && this.#stringStorage) {
            // Storage was externally provided (non-pdb), so index here
            // Another reason to fully decouple from PDB and allow raw values and also more customizable external indexing
            const storage = this.#stringStorage;
            this.index({
                getAttrValue: (attrId, valId) => storage.get(valId)
            }, storage, new Map());
        }
        if (!this.#descriptor) {
            throw new Error(`Attempted to transfer string column when not indexed: ${this.#attr.name}`);
        }
        return this.#descriptor;
    }
    getDictionary() {
        return this.#stringStorage;
    }
    setDictionary(instance) {
        this.#stringStorage = instance;
    }
    asDictionaryType() {
        return this;
    }
    byteCount() {
        return this.#byteCount;
    }
}
export class CustomPropertyWrapperColumn {
    #wrapped;
    get idCount() { return this.#wrapped.idCount; }
    get isSparse() { return this.#wrapped.isSparse; }
    /**
     * Custom properties are technically applied to all objects in the model.
     * There are no undefined values, and no matter what the data-type, will return an empty string in that scenario
     * To preserve the space saving of the original columns, this simply wraps another column and returns empty string when required
     * @param wrapped
     */
    constructor(wrapped) {
        this.#wrapped = wrapped;
    }
    getValueAt(index) {
        const val = this.#wrapped.getValueAt(index);
        if (val === undefined || val === null) {
            return "";
        }
        return val;
    }
    setValueAt() {
        fail("Not supported");
    }
    isNull(index) {
        return this.#wrapped.isNull(index);
    }
    index() {
        fail("Not supported");
    }
    valueIdAtIndex(index) {
        return this.#wrapped?.valueIdAtIndex(index) ?? fail(`Not dictionary encoded`);
    }
    toWireType() {
        return this.#wrapped.toWireType();
    }
    getDictionary() {
        return this.#wrapped.isStringType
            ? this.#wrapped.getDictionary()
            : undefined;
    }
    setDictionary(instance) {
        if (this.#wrapped.isStringType) {
            this.#wrapped.setDictionary(instance);
        }
    }
    asDictionaryType() {
        return this.#wrapped.asDictionaryType();
    }
    byteCount() {
        return this.#wrapped.byteCount() + 16;
    }
    static wrap(otherColumn) {
        return new CustomPropertyWrapperColumn(otherColumn);
    }
}
function getMask(index, view) {
    const byte = view.getUint8(Math.floor(index / 8));
    const mask = 1 << index % 8;
    return Boolean(byte & mask);
}
function setMask(index, value, view) {
    const byteIdx = Math.floor(index / 8);
    const byte = view.getUint8(byteIdx);
    let mask = 1 << index % 8;
    if (!value)
        mask = ~mask;
    view.setUint8(byteIdx, value ? byte | mask : byte & mask);
}
